import { Alert, AlertTitle, Button, IconButton } from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid-premium';
import { cloneDeep } from 'lodash';
import React, { useContext, useMemo, useRef } from 'react';
import type { Unit } from 'sr-types/lib/search/v1/graphql';
import { useUnitsData } from '../../attributes/attributesApi';
import { SECURITY_ACTIONS, useSecurity } from '../../common/auth/Security';
import { FILTER_IDENTIFIERS } from '../../common/constants/constants';
import { FEATURE_CONSTANTS } from '../../common/constants/featureConstants';
import { FORM_WIDGET_COMPONENTS, METADATA_NAMES } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormPanel from '../../common/form/FormPanel';
import FormWidget from '../../common/form/FormWidget';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import I18n, { useI18n } from '../../common/i18n/I18n';
import { Icons } from '../../common/icons/Icons';
import PersonOptionRenderer from '../../shortlists/form/PersonOptionRenderer';
import { ProductionOptionRenderer } from '../../supply/booking/ProductionOptionRenderer';
import ProductOptionRenderer, { getOption } from '../../supply/product/ProductOptionRenderer';
import { ActivityOrder, Activity_Field_Names, EMPTY_DEPENDENCIES } from '../helpers/activityUtils';
import { autoCreateQuery as autoCreateProductionQuery, client as productionClient } from '../helpers/production';
import { quickAddProduction } from '../helpers/QuickAddProduction';
import { ACTIVITY_DEPENDENCIES, VALID_UNITS } from './activityConstants';
import ActivityScheduleFormWidget from './ActivityScheduleFormWidget';

export type ActivityFormItemType = {
    showBasic: boolean;
    isResourceForm: boolean;
    onCreateOwner?: any;
    resourceOptions?: any;
    activitiesLoading?: boolean;
    activityOptions?: any;
    disabledFields?: Record<string, boolean>;
    hiddenFields?: Record<string, boolean>;
    hasError?: boolean;
};

export const ActivityFormItems = ({
    showBasic,
    isResourceForm,
    onCreateOwner,
    resourceOptions,
    activitiesLoading,
    activityOptions,
    disabledFields = {},
    hiddenFields = {},
    hasError = false
}: ActivityFormItemType) => {
    const { setState, state } = useContext(FormContext);

    // Fetch units data once for both schedule widget and dependency lag units
    const { unitsData, unitsLoading } = useUnitsData('Time');

    // Extract raw units for schedule widget
    const durationUnits = useMemo<Unit[]>(() => unitsData?.results?.hits?.items || [], [unitsData]);

    // Transform units for dependency lag unit dropdowns
    const unitOptions = useMemo(() => {
        if (unitsData?.results?.hits?.items) {
            return unitsData.results.hits.items
                .filter((item) => VALID_UNITS.includes(item.label))
                .map((item) => ({ id: item.label, label: item.label }));
        }
        return [];
    }, [unitsData]);

    const usedOptions = useMemo(() => {
        if (state?.dependencies) return Object.keys(state.dependencies);
    }, [state?.dependencies]);
    const lastElement = useRef(null);
    const [saveProduction] = useMutationWithContext(autoCreateProductionQuery, productionClient);
    const scrollToBottom = () => {
        lastElement.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const { allowed: canManageResources } = useSecurity(SECURITY_ACTIONS.EDIT, {
        featureName: FEATURE_CONSTANTS.resources
    });

    const locationsLabel = useI18n('production.activityForm.locations');
    const categoryLabel = useI18n('production.activityForm.category');
    const associateWithResourceLabel = useI18n('production.activityForm.associateWithResource');
    const productsLabel = useI18n('common.label.products');
    const activeOrganizationOwnerLabel = useI18n('production.activityForm.activityOrganizationOwner');
    const activityErrorTitleLabel = useI18n('form.activity.error.title');
    const activityErrorDescriptionLabel = useI18n('form.activity.error.description');
    const summaryLabel = useI18n('common.summary');
    const isScheduleDisabled = Boolean(disabledFields[Activity_Field_Names.dateRange]);

    return (
        <FormPanel>
            <FormContainer
                titleI18nToken={'production.activityForm.basic'}
                hideTitle={hiddenFields[Activity_Field_Names.basicSection]}
                showDivider={!hiddenFields[Activity_Field_Names.basicSection]}
            >
                <FormItem hidden={!hasError}>
                    <Alert severity="error" sx={{ width: '100%' }}>
                        <AlertTitle>{activityErrorTitleLabel}</AlertTitle>
                        {activityErrorDescriptionLabel}
                    </Alert>
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.name]}>
                    <FormWidget
                        name={Activity_Field_Names.name}
                        label={useI18n('production.activityForm.name')}
                        disabled={disabledFields[Activity_Field_Names.name]}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.activityType]}>
                    <FormWidget
                        name={Activity_Field_Names.activityType}
                        component="MetadataAutocomplete"
                        label={useI18n('production.activityForm.type')}
                        metadataName={'ActivityType'}
                        disabled={disabledFields[Activity_Field_Names.activityType]}
                    />
                </FormItem>
            </FormContainer>

            <FormContainer
                titleI18nToken={'production.activityForm.schedule'}
                hideTitle={hiddenFields[Activity_Field_Names.scheduleSection]}
                showDivider={!hiddenFields[Activity_Field_Names.scheduleSection]}
            >
                <ActivityScheduleFormWidget isScheduleDisabled={isScheduleDisabled} durationUnits={durationUnits} />
            </FormContainer>

            <FormContainer
                titleI18nToken="production.activityForm.details"
                hideTitle={hiddenFields[Activity_Field_Names.detailsSection]}
                hidden={showBasic}
                showDivider={!hiddenFields[Activity_Field_Names.detailsSection]}
            >
                <FormItem hidden={hiddenFields[Activity_Field_Names.productionReference]}>
                    <FormWidget
                        name={Activity_Field_Names.productionReference}
                        addOption
                        component="ReferenceAutocomplete"
                        entity="Production"
                        label={<I18n token="production.form.field.name" />}
                        OptionRenderer={ProductionOptionRenderer}
                        onCreateHandle={quickAddProduction}
                        saveMutation={saveProduction}
                        disabled={disabledFields[Activity_Field_Names.productionReference]}
                        preload={false}
                    />
                </FormItem>
                <FormItem hidden={hiddenFields[Activity_Field_Names.productReferences]}>
                    <FormWidget
                        component="ReferenceAutocomplete"
                        name={Activity_Field_Names.productReferences}
                        entity="Product"
                        label={productsLabel}
                        multiple
                        disabled={disabledFields[Activity_Field_Names.productReferences]}
                        OptionRenderer={ProductOptionRenderer}
                        getOption={getOption}
                        preload={false}
                        sortBy={'name_ASC,ownerOrganizationName_ASC'}
                    />
                </FormItem>
                <FormItem hidden={hiddenFields[Activity_Field_Names.organizationReference]}>
                    <FormWidget
                        name={Activity_Field_Names.organizationReference}
                        component="ReferenceAutocomplete"
                        entity="Organization"
                        label={activeOrganizationOwnerLabel}
                        disabled={disabledFields[Activity_Field_Names.organizationReference]}
                        preload={false}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.locations]}>
                    <FormWidget
                        component="Location"
                        name={Activity_Field_Names.locations}
                        label={locationsLabel}
                        multiple
                        disabled={disabledFields[Activity_Field_Names.locations]}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.category]}>
                    <FormWidget
                        component="MetadataAutocomplete"
                        metadataName="Category"
                        name={Activity_Field_Names.category}
                        label={categoryLabel}
                        disabled={disabledFields[Activity_Field_Names.category]}
                    />
                </FormItem>
                <FormItem hidden={hiddenFields[Activity_Field_Names['summary.about']]}>
                    <FormWidget
                        name={Activity_Field_Names['summary.about']}
                        component={FORM_WIDGET_COMPONENTS.textArea}
                        label={summaryLabel}
                        disabled={disabledFields[Activity_Field_Names['summary.about']]}
                    />
                </FormItem>
            </FormContainer>

            <FormContainer
                titleI18nToken="production.form.title.track"
                hideTitle={hiddenFields[Activity_Field_Names.trackOwnerStatusSection]}
                hidden={showBasic}
                showDivider={!hiddenFields[Activity_Field_Names.trackOwnerStatusSection]}
            >
                <FormItem
                    hidden={
                        hiddenFields[Activity_Field_Names.associateWithResource] ||
                        isResourceForm ||
                        !canManageResources
                    }
                >
                    <FormWidget
                        component="Autocomplete"
                        name={Activity_Field_Names.associateWithResource}
                        label={associateWithResourceLabel}
                        options={resourceOptions}
                        multiple
                        disabled={disabledFields[Activity_Field_Names.associateWithResource]}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.activityOwner]}>
                    <FormWidget
                        component="ReferenceAutocomplete"
                        entity="Person"
                        name={Activity_Field_Names.activityOwner}
                        label={<I18n token="production.form.field.owner" />}
                        addOption
                        OptionRenderer={PersonOptionRenderer}
                        getOption={(item) => {
                            return {
                                id: item.id,
                                name: item.name,
                                role: item.role,
                                label: item.name,
                                ref: { ...item.reference, label: item.name },
                                professionalRoles: item.professionalRoles
                            };
                        }}
                        onCreateHandle={onCreateOwner}
                        preload={false}
                        disabled={disabledFields[Activity_Field_Names.activityOwner]}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.activityStatus]}>
                    <FormWidget
                        component="MetadataAutocomplete"
                        metadataName={METADATA_NAMES.activityStatus}
                        name={Activity_Field_Names.activityStatus}
                        label={<I18n token="production.form.field.status" />}
                        allowCreate
                        disabled={disabledFields[Activity_Field_Names.activityStatus]}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Activity_Field_Names.tags]}>
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName={METADATA_NAMES.documentTag}
                        identifier={FILTER_IDENTIFIERS.termset}
                        name={Activity_Field_Names.tags}
                        label={<I18n token="common.label.tags" />}
                        multiple
                        allowCreate
                        disabled={disabledFields[Activity_Field_Names.tags]}
                    />
                </FormItem>
            </FormContainer>
            <FormContainer>
                <>
                    {Object.keys(state.dependencies)
                        .filter((key) => key !== ACTIVITY_DEPENDENCIES.lastDependency)
                        .map((item) => (
                            <ActivityDependencyFormItems
                                activitiesLoading={activitiesLoading}
                                activityOptions={activityOptions}
                                unitOptions={unitOptions}
                                unitsLoading={unitsLoading}
                                hidden={hiddenFields[Activity_Field_Names.activityDependency] || showBasic}
                                id={item}
                                key={item}
                                onDelete={(id: any) => {
                                    setState((prevVal) => {
                                        const { [id]: removedDependency, ...otherDependencies } = prevVal.dependencies;
                                        return { ...prevVal, dependencies: { ...otherDependencies } };
                                    });
                                }}
                            />
                        ))}
                </>

                <ActivityDependencyFormItems
                    activitiesLoading={activitiesLoading}
                    activityOptions={
                        activityOptions ? activityOptions.filter((option) => !usedOptions.includes(option.id)) : []
                    }
                    unitOptions={unitOptions}
                    unitsLoading={unitsLoading}
                    hidden={hiddenFields[Activity_Field_Names.activityDependency] || showBasic}
                    id={ACTIVITY_DEPENDENCIES.lastDependency}
                    onDelete={() => {
                        setState((prevVal) => {
                            return {
                                ...prevVal,
                                dependencies: {
                                    ...prevVal.dependencies,
                                    ...cloneDeep(EMPTY_DEPENDENCIES)
                                }
                            };
                        });
                    }}
                />

                <FormItem hidden={hiddenFields[Activity_Field_Names.addActivityButton] || showBasic}>
                    <Button
                        ref={lastElement}
                        sx={{ alignSelf: 'start' }}
                        disabled={
                            activityOptions
                                ? activityOptions.filter((option) => !usedOptions.includes(option.id)).length <= 1 ||
                                  !state.dependencies?.lastDependency?.activityRefId
                                : true
                        }
                        onClick={() => {
                            setState((prevVal) => {
                                return {
                                    ...prevVal,
                                    dependencies: {
                                        ...prevVal.dependencies,
                                        ...cloneDeep(EMPTY_DEPENDENCIES),
                                        [prevVal.dependencies.lastDependency.activityRefId]: {
                                            ...prevVal.dependencies.lastDependency
                                        }
                                    }
                                };
                            });
                            setTimeout(() => {
                                scrollToBottom();
                            }, 300);
                        }}
                    >
                        <Icons.AddButton /> <I18n token={'production.activity'} />
                    </Button>
                </FormItem>
            </FormContainer>
        </FormPanel>
    );
};

const ActivityDependencyFormItems = ({
    id,
    onDelete,
    activityOptions,
    activitiesLoading,
    unitOptions,
    unitsLoading,
    hidden = false
}) => {
    const { handleChange, state } = useContext(FormContext);

    return hidden ? (
        <React.Fragment />
    ) : (
        <>
            <FormItem>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.toggleButtonGroup}
                    color="primary"
                    value={state?.dependencies?.[id]?.order}
                    exclusive
                    sx={{ mb: 2 }}
                    onChange={(e, val) => {
                        handleChange(`dependencies.${id}.order`, val);
                    }}
                    options={[
                        { id: ActivityOrder.predecessor, label: <I18n token="form.activity.predecessor" /> },
                        { id: ActivityOrder.successor, label: <I18n token="form.activity.successor" /> }
                    ]}
                ></FormWidget>
            </FormItem>
            <FormItem>
                <FormWidget
                    label={<I18n token="production.activity" />}
                    name={`dependencies.${id}.activityRefId`}
                    component={FORM_WIDGET_COMPONENTS.autocomplete}
                    loading={activitiesLoading}
                    options={activityOptions}
                    disableClearable
                    disabled={id !== ACTIVITY_DEPENDENCIES.lastDependency}
                />
                <IconButton
                    size="small"
                    color="inherit"
                    sx={{ height: 'fit-content' }}
                    onClick={() => {
                        onDelete(id);
                    }}
                >
                    <GridCloseIcon />
                </IconButton>
            </FormItem>
            <FormItem half>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.number}
                    name={`dependencies.${id}.lag`}
                    label={<I18n token={'production.activityForm.dependencyFollowBy'} />}
                    forcePositive={true}
                />
            </FormItem>
            <FormItem half>
                <FormWidget
                    label={<I18n token="production.activityForm.dependencyUnit" />}
                    name={`dependencies.${id}.lagUnit`}
                    component={FORM_WIDGET_COMPONENTS.autocomplete}
                    loading={unitsLoading}
                    options={unitOptions}
                    disableClearable
                />
            </FormItem>
            <FormItem>
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    name={`dependencies.${id}.type`}
                    metadataName="DependencyType"
                    label={<I18n token="production.activityForm.dependencyType" />}
                    identifier="termset"
                />
            </FormItem>
        </>
    );
};
